import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import SuperAdminLayout from '../components/SuperAdminLayout';
import ServerSelector from './components/ServerSelector';
import ServersTab from './components/ServersTab';
import RankupTab from './components/RankupTab';
import ActivenessTab from './components/ActivenessTab';
import IncrementalTab from './components/IncrementalTab';
import RecruitmentTab from './components/RecruitmentTab';
import MetricThresholdsTab from './components/MetricThresholdsTab';
import TargetsTemplateTab from './components/TargetsTemplateTab';
import IndicatorsRegistryTab from './components/IndicatorsRegistryTab';

/**
 * Super Admin — Business Rules console (/superadmin/business-rules).
 *
 * Layout:
 *   - Global server selector (drives the data view of every tab except Servers and Indicators)
 *   - 7 tabs:
 *       servers       — CRUD on public.servers
 *       rankup        — public.rankup_config (10 tiers)
 *       activeness    — public.activeness_config (5 levels)
 *       incremental   — public.incremental_config (revenue paliers)
 *       thresholds    — public.metric_thresholds (Module 1 Key Metrics + others)
 *       targets       — public.agent_targets_template
 *       indicators    — read-only public.metric_formulas
 *
 * R2 reminder: this page deals only with system-level rules — no financial data.
 */

const TABS = [
  { id: 'servers',     label: 'Servers',          serverScoped: false },
  { id: 'rankup',      label: 'Rank-up tiers',    serverScoped: true },
  { id: 'activeness',  label: 'Activeness',       serverScoped: true },
  { id: 'incremental', label: 'Revenue paliers',  serverScoped: true },
  { id: 'recruitment', label: 'Recruitment bonus', serverScoped: false },
  { id: 'thresholds',  label: 'Metric thresholds', serverScoped: true },
  { id: 'targets',     label: 'Targets template', serverScoped: true },
  { id: 'indicators',  label: 'Indicators',       serverScoped: false },
];

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-accent text-accent'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

const BusinessRulesPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [servers, setServers] = useState([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [serversError, setServersError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const initialTab = searchParams.get('tab');
  const initialServer = searchParams.get('server');
  const [tab, setTab] = useState(
    TABS.find((x) => x.id === initialTab)?.id || 'servers'
  );
  const [selectedServer, setSelectedServer] = useState(initialServer || 'MENA');

  const loadServers = useCallback(async () => {
    setLoadingServers(true);
    setServersError(null);
    const { data, error } = await supabase
      .from('servers')
      .select('*')
      .order('id', { ascending: true });
    if (error) {
      // RLS blocks non-super_admin. Detect this and show access-denied state.
      const msg = (error.message || '').toLowerCase();
      if (
        error.code === 'PGRST301' ||
        msg.includes('permission') ||
        msg.includes('rls') ||
        msg.includes('not allowed')
      ) {
        setAccessDenied(true);
      }
      setServersError(error.message);
      setServers([]);
    } else {
      setServers(data || []);
      // Ensure selected server still exists
      if (data && data.length > 0 && !data.find((s) => s.id === selectedServer)) {
        setSelectedServer(data[0].id);
      }
    }
    setLoadingServers(false);
  }, [selectedServer]);

  useEffect(() => {
    loadServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync (so the page is shareable / refresh-safe).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (selectedServer) next.set('server', selectedServer);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedServer]);

  if (accessDenied) {
    return (
      <SuperAdminLayout
        title={t('superadmin.businessRules.title', 'Business Rules')}
        subtitle={t(
          'superadmin.businessRules.subtitle',
          'System-level configuration — multi-server'
        )}
      >
        <div className="rounded-2xl bg-card border border-error/30 p-8 text-center">
          <div className="font-heading font-semibold text-error mb-2">
            {t('superadmin.accessDenied', 'Access denied')}
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {t(
              'superadmin.businessRules.rlsDenied',
              'You do not have permission to view or edit business rules. This console is restricted to super admins.'
            )}
          </p>
        </div>
      </SuperAdminLayout>
    );
  }

  const currentTab = TABS.find((x) => x.id === tab) || TABS[0];

  return (
    <SuperAdminLayout
      title={t('superadmin.businessRules.title', 'Business Rules')}
      subtitle={t(
        'superadmin.businessRules.subtitle',
        'System-level configuration — multi-server'
      )}
    >
      {/* Server selector + R2 reminder */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        {currentTab.serverScoped ? (
          <ServerSelector
            servers={servers}
            value={selectedServer}
            onChange={setSelectedServer}
            loading={loadingServers}
          />
        ) : (
          <div className="text-xs text-muted-foreground italic">
            {t(
              'superadmin.businessRules.notServerScoped',
              'This tab is global (not server-scoped).'
            )}
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest text-accent font-semibold">
          {t(
            'superadmin.businessRules.r2',
            'R2 — system rules only, no financial data'
          )}
        </div>
      </div>

      {serversError && !accessDenied && (
        <div className="mb-4 rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {serversError}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((x) => (
            <TabButton
              key={x.id}
              active={tab === x.id}
              onClick={() => setTab(x.id)}
            >
              {t(`superadmin.businessRules.tab.${x.id}`, x.label)}
            </TabButton>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === 'servers' && (
        <ServersTab
          servers={servers}
          loading={loadingServers}
          onChange={loadServers}
        />
      )}
      {tab === 'rankup' && <RankupTab server={selectedServer} />}
      {tab === 'activeness' && <ActivenessTab server={selectedServer} />}
      {tab === 'incremental' && <IncrementalTab server={selectedServer} />}
      {tab === 'recruitment' && <RecruitmentTab server={selectedServer} />}
      {tab === 'thresholds' && <MetricThresholdsTab server={selectedServer} />}
      {tab === 'targets' && <TargetsTemplateTab server={selectedServer} />}
      {tab === 'indicators' && <IndicatorsRegistryTab />}
    </SuperAdminLayout>
  );
};

export default BusinessRulesPage;
